/**
 * 存貨紀錄時間軸 (REQ-20260421010)。
 * 結構對齊 InterviewTimeline:標題列 + 新增按鈕 + 無限捲動列表。
 */
import { useEffect, useRef, useState } from 'react';
import type { InventoryRecordResponse } from '@app/shared';
import { useInventoryImageUrl, useInventoryRecords } from './api';
import { InventoryRecordModal } from './InventoryRecordModal';
import { useAuthStore } from '../auth/store';

interface Props {
  customerId: number;
  customerOwnerId: number;
}

export function InventoryTimeline({ customerId, customerOwnerId }: Props): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const canWrite =
    me?.role === 'ADMIN' || (me?.role === 'SALES' && customerOwnerId === me.id);

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInventoryRecords(customerId);

  const records = data?.pages.flatMap((p) => p.data) ?? [];
  const total = data?.pages[0]?.meta.total ?? 0;

  const [modal, setModal] = useState<
    { mode: 'new' | 'edit'; record?: InventoryRecordResponse } | null
  >(null);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <section data-testid="inventory-timeline">
      <header className="mb-3 flex items-center justify-between min-h-[2.25rem]">
        <h2 className="text-lg font-semibold" data-testid="inventory-count">
          存貨紀錄（{isLoading ? '…' : total}）
        </h2>
        {canWrite ? (
          <button
            type="button"
            onClick={() => setModal({ mode: 'new' })}
            className="bg-blue-600 text-white rounded px-3 py-1.5 text-sm hover:bg-blue-700"
            data-testid="new-inventory-btn"
          >
            新增存貨
          </button>
        ) : null}
      </header>

      {isLoading ? (
        <p className="text-slate-500">存貨紀錄載入中…</p>
      ) : isError ? (
        <p role="alert" className="text-red-600">存貨紀錄載入失敗</p>
      ) : records.length === 0 ? (
        <p className="text-slate-500">目前沒有存貨紀錄</p>
      ) : null}

      <ol className="grid gap-3">
        {records.map((rec) => (
          <InventoryItem
            key={rec.id}
            rec={rec}
            canWrite={canWrite}
            onEdit={() => setModal({ mode: 'edit', record: rec })}
          />
        ))}
      </ol>

      {hasNextPage ? (
        <div
          ref={sentinelRef}
          data-testid="inventory-load-more-sentinel"
          className="text-center text-slate-500 text-sm py-3"
        >
          {isFetchingNextPage ? '載入更多…' : '下滑載入更多'}
        </div>
      ) : null}

      {modal ? (
        <InventoryRecordModal
          open={true}
          onClose={() => setModal(null)}
          customerId={customerId}
          mode={modal.mode}
          record={modal.record ?? null}
        />
      ) : null}
    </section>
  );
}

/** "5.0000" → "5";"5.2500" → "5.25";保留有效小數。 */
function formatQuantity(q: string): string {
  if (!q) return q;
  if (!q.includes('.')) return q;
  return q.replace(/\.?0+$/, '') || '0';
}

function InventoryItem({
  rec,
  canWrite,
  onEdit,
}: {
  rec: InventoryRecordResponse;
  canWrite: boolean;
  onEdit: () => void;
}): JSX.Element {
  const imgQuery = useInventoryImageUrl(rec.id, !!rec.attachment);
  return (
    <li
      className="bg-white border border-slate-200 rounded-md p-3 flex gap-3 transition-colors duration-150 hover:border-emerald-400 hover:shadow-sm"
      data-testid={`inventory-item-${rec.id}`}
    >
      <div className="shrink-0 w-24 h-24 bg-slate-100 rounded flex items-center justify-center overflow-hidden">
        {rec.attachment && imgQuery.data ? (
          <a href={imgQuery.data} target="_blank" rel="noopener noreferrer">
            <img
              src={imgQuery.data}
              alt={rec.attachment.originalName}
              className="w-24 h-24 object-cover"
            />
          </a>
        ) : (
          <span className="text-xs text-slate-400">無圖片</span>
        )}
      </div>
      <div className="flex-1 min-w-0 grid gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-base font-semibold text-slate-900 truncate">
              {rec.itemName}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              by {rec.createdBy.displayName}
            </div>
          </div>
          {canWrite ? (
            <button
              type="button"
              onClick={onEdit}
              className="text-sm border rounded px-2 py-0.5 hover:bg-slate-100 shrink-0"
            >
              編輯
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <div>
            <span className="text-slate-500">入庫日期:</span>{' '}
            <span className="text-slate-800">{rec.recordedAt}</span>
          </div>
          <div>
            <span className="text-slate-500">數量:</span>{' '}
            <span className="text-slate-800 font-medium">
              {formatQuantity(rec.quantity)}
              {rec.unit ? ` ${rec.unit}` : ''}
            </span>
          </div>
        </div>
        {rec.notes ? (
          <div className="text-sm text-slate-700 bg-slate-50 rounded px-2 py-1 whitespace-pre-wrap">
            <span className="text-xs text-slate-500 mr-1">備註:</span>
            {rec.notes}
          </div>
        ) : null}
      </div>
    </li>
  );
}
